import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

import { db } from '../../firebase.js';
import { normalizeCompanyIds } from '../../common/wasteReceivers.js';

const normalizeIdList = (ids = []) => normalizeCompanyIds(ids);

const SETUP_ENTITY_TYPES = {
    WASTE_RECEIVER: 'waste_receiver',
    COMPANY: 'company',
    USER: 'user',
};

const normalizeCompanyMembershipPermissionEntry = (value = {}) => ({
    canViewAllCompanyData:
        value?.can_view_all_company_data === true ||
        value?.canViewAllCompanyData === true,
    canViewPhysicalMachineData:
        value?.can_view_physical_machine_data === true ||
        value?.canViewPhysicalMachineData === true,
    canViewWasteReceiverSortingList:
        value?.can_view_waste_receiver_sorting_list === true ||
        value?.canViewWasteReceiverSortingList === true,
});

const normalizeUserCompanyMemberships = (memberships) => {
    if (!memberships || typeof memberships !== 'object' || Array.isArray(memberships)) {
        return {};
    }

    return Object.entries(memberships).reduce((next, [companyId, value]) => {
        const normalizedCompanyId = String(companyId || '').trim();
        if (!normalizedCompanyId) return next;
        next[normalizedCompanyId] = normalizeCompanyMembershipPermissionEntry(value);
        return next;
    }, {});
};

const serializeCompanyMembershipPermissionEntry = (value = {}) => {
    const normalized = normalizeCompanyMembershipPermissionEntry(value);
    return {
        can_view_all_company_data: normalized.canViewAllCompanyData,
        can_view_physical_machine_data: normalized.canViewPhysicalMachineData,
        can_view_waste_receiver_sorting_list: normalized.canViewWasteReceiverSortingList,
    };
};

const areCompanyMembershipPermissionsEqual = (left = {}, right = {}) => {
    const normalizedLeft = normalizeCompanyMembershipPermissionEntry(left);
    const normalizedRight = normalizeCompanyMembershipPermissionEntry(right);

    return (
        normalizedLeft.canViewAllCompanyData === normalizedRight.canViewAllCompanyData &&
        normalizedLeft.canViewPhysicalMachineData === normalizedRight.canViewPhysicalMachineData &&
        normalizedLeft.canViewWasteReceiverSortingList === normalizedRight.canViewWasteReceiverSortingList
    );
};

function getCompanyLabelFromData(companyId, data = {}) {
    return data.name || data.display_name || data.label || companyId;
}

function normalizeCompanyDoc(snapshot) {
    const data = snapshot.data() || {};

    return {
        id: snapshot.id,
        label: getCompanyLabelFromData(snapshot.id, data),
        testCompany:
            data.test_company === true ||
            data.testCompany === true,
        // Companies predate the `active` field, so anything but an explicit false is active.
        active: data.active !== false,
        wasteReceiver:
            typeof data.waste_receiver === 'string'
                ? data.waste_receiver.trim()
                : '',
    };
}

function getUserLabelFromData(userId, data = {}) {
    return data.name || data.display_name || data.email || data.user_id || userId;
}

function normalizeUserDoc(snapshot) {
    const data = snapshot.data() || {};
    const email = data.email || data.mail || '';

    return {
        id: snapshot.id,
        label: getUserLabelFromData(snapshot.id, data),
        email,
        secondaryLabel: email || data.user_id || snapshot.id,
        companies: normalizeIdList(data.companies),
        wasteReceivers: normalizeIdList(data.waste_receivers),
        companyMemberships: normalizeUserCompanyMemberships(data.company_memberships),
        isAdmin: data.isAdmin === true,
        welcomeEmails: Array.isArray(data.welcome_emails) ? data.welcome_emails : [],
    };
}

async function loadCompanyOptions(userData) {
    try {
        const snap = await getDocs(collection(db, 'companies'));
        return snap.docs
            .map(normalizeCompanyDoc)
            .sort((a, b) => a.label.localeCompare(b.label));
    } catch (error) {
        console.error('Error loading companies collection:', error);
    }

    const companyIds = normalizeCompanyIds(userData?.companies || []);
    const loadedCompanies = await Promise.all(
        companyIds.map(async (companyId) => {
            try {
                const snap = await getDoc(doc(db, 'companies', companyId));
                return {
                    id: companyId,
                    label: snap.exists()
                        ? getCompanyLabelFromData(companyId, snap.data())
                        : companyId,
                    testCompany:
                        snap.exists() && (
                            snap.data()?.test_company === true ||
                            snap.data()?.testCompany === true
                        ),
                    active: !snap.exists() || snap.data()?.active !== false,
                    wasteReceiver:
                        snap.exists() && typeof snap.data()?.waste_receiver === 'string'
                            ? snap.data().waste_receiver.trim()
                            : '',
                };
            } catch (error) {
                console.error('Error loading company:', error);
                return {
                    id: companyId,
                    label: companyId,
                    testCompany: false,
                    active: true,
                    wasteReceiver: '',
                };
            }
        }),
    );

    return loadedCompanies.sort((a, b) => a.label.localeCompare(b.label));
}

export {
    areCompanyMembershipPermissionsEqual,
    getCompanyLabelFromData,
    getUserLabelFromData,
    loadCompanyOptions,
    normalizeCompanyDoc,
    normalizeCompanyMembershipPermissionEntry,
    normalizeIdList,
    normalizeUserCompanyMemberships,
    normalizeUserDoc,
    serializeCompanyMembershipPermissionEntry,
    SETUP_ENTITY_TYPES,
};
